<template>
  <PriorHeader />
  <div class="submission-status-wrapper">
    <div class="page-header">
      <h1>All Submissions</h1>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="router.push({ name: 'create-submission' })">
        Create Submission
      </v-btn>
    </div>
    <div class="search-container">
      <form @submit.prevent="runSearch">
        <div class="lh--search-container">
          <label for="search-term" class="text-body-3-2 weight-600"> Search by Keywords in Submission </label>
          <v-text-field
            id="search-term"
            v-model="searchFormData.searchTerm"
            class="lh-input"
            type="text"
            density="comfortable"
            prepend-inner-icon="mdi-magnify"
            placeholder="eg., Keycode, Medication, Patient Name, Payor, Provider" />
        </div>
        <div class="lh--filter-container">
          <div class="lh--status-filter">
            <v-select
              v-model="searchFormData.status"
              :items="statusOptionsWithCounts"
              label="Filter by Status"
              density="comfortable"
              placeholder="All Submissions" />
          </div>
          <div class="lh--date-filter">
            <v-select
              v-model="searchFormData.orderBy"
              :items="orderingOptions"
              label="Filter by Date"
              placeholder="Most Recent"
              density="comfortable" />
          </div>
        </div>
        <div class="lh--buttons-container">
          <v-btn type="submit">Search</v-btn>
          <v-btn variant="outlined" @click="clearSearch">Clear search</v-btn>
        </div>
      </form>
    </div>
    <div v-if="preloader" class="lh--available-orders-container">
      <div v-for="n in 5" :key="n">
        <v-card class="card">
          <div class="card-container">
            <v-skeleton-loader type="heading" class="lh--patient-name skeleton-name mb-4"></v-skeleton-loader>
            <div class="lh--order-details">
              <div v-for="i in 8" :key="i" class="lh--order-details-item">
                <div class="lh--order-details-item-label">
                  <v-skeleton-loader type="text" width="80px"></v-skeleton-loader>
                </div>
                <div class="lh--order-details-item-value">
                  <v-skeleton-loader type="text" width="150px"></v-skeleton-loader>
                </div>
              </div>
            </div>
          </div>
          <div class="buttons-container">
            <v-skeleton-loader type="button" class="mr-2"></v-skeleton-loader>
            <v-skeleton-loader type="button" class="mr-2"></v-skeleton-loader>
          </div>
        </v-card>
      </div>
    </div>
    <div v-if="!preloader && availableOrders.length === 0" class="lh--available-orders-container p-0">
      <h2>No Submissions Available</h2>
    </div>
    <div v-if="!preloader && availableOrders.length > 0" class="lh--available-orders-container">
      <SubmissionDetailCard
        v-for="order in availableOrders"
        :key="order.orderKey"
        :order="order"
        :fax-forms="faxDestinationOptions"
        @on-delete="onOrderDelete"
        @on-upload-decision="onUploadDecision" />
    </div>
  </div>
  <div v-if="!preloader" class="pagination">
    <div v-for="index in displayedPages" :key="index">
      <button
        v-if="typeof index === 'number'"
        class="page-button"
        :class="{ active: index === currentPage }"
        @click="getAvailableOrders(index)">
        {{ index }}
      </button>
      <span v-else>
        {{ index }}
      </span>
    </div>
  </div>
  <PriorFooter />
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import PriorHeader from "@/components/PriorHeader";
import PriorFooter from "@/components/PriorFooter";
import { orderInfoService } from "@/services/orderInfoService";
import SubmissionDetailCard from "@/components/SubmissionDetailCard.vue";
import { useRoute, useRouter } from "vue-router";
import { mainServices } from "@/services/mainServices";

const route = useRoute();
const router = useRouter();
const availableOrders = ref([]);
const preloader = ref(false);
const pageCount = ref(1);
const currentPage = ref(1);
const statusCounts = ref([]);
const faxDestinationOptions = ref([]);

const searchFormData = ref({
  searchTerm: "",
  status: "",
  orderBy: "",
});

const statusOptions = ref([
  { title: "All Submissions", value: "" },
  { title: "Initiated", value: "INITIATED" },
  { title: "Fetching questions", value: "CREATED" },
  { title: "Incomplete", value: "INCOMPLETE" },
  { title: "Payor/Medication not supported", value: "NOT_SUPPORTED" },
  { title: "Requires review", value: "REQUIRES_REVIEW" },
  { title: "Review demographics info", value: "DEMOGRAPHICS_REVIEW" },
  { title: "Review clinical info", value: "CLINICAL_REVIEW" },
  { title: "Processing", value: "PROCESSING" },
  { title: "Sent to prescriber", value: "SENT_TO_PRESCRIBER" },
  { title: "Sent to plan", value: "SENT_TO_PLAN" },
  { title: "No payor response received", value: "NO_PAYOR_RESPONSE_RECEIVED" },
  { title: "Requires more information", value: "REQUIRES_MORE_INFORMATION" },
  { title: "Response received", value: "RESPONSE_RECEIVED" },
  { title: "Approved", value: "APPROVED" },
  { title: "Denied", value: "DENIED" },
  { title: "Archived", value: "ARCHIVED" },
  { title: "Prior authorization not required", value: "PA_NOT_REQUIRED" },
]);

const orderingOptions = ref([
  { title: "Most Recent", value: "-created_on" },
  { title: "Oldest", value: "created_on" },
  { title: "Last Modified", value: "-modified_on" },
  { title: "Status Last Modified", value: "-status_modified_on" },
  { title: "Status Oldest Modified", value: "status_modified_on" },
]);

onMounted(() => {
  if (route.query.search) {
    searchFormData.value.searchTerm = route.query.search;
    searchRunning.value = true;
  }
  if (route.query.status) {
    const queryStatus = route.query.status.toString().toUpperCase();
    const validStatus = statusOptions.value.find((option) => option.title.toUpperCase() === queryStatus);
    if (validStatus) {
      searchFormData.value.status = validStatus.value;
      searchRunning.value = true;
    }
  }
  if (route.query.orderBy) {
    const queryOrderBy = route.query.orderBy.toString().toUpperCase();
    const validOrderBy = orderingOptions.value.find((option) => option.title.toUpperCase() === queryOrderBy);
    if (validOrderBy) {
      searchFormData.value.orderBy = validOrderBy.value;
      searchRunning.value = true;
    }
  }
  loadStatusCounts();
  getAvailableOrders(currentPage.value);
  getFaxForms();
});

const searchRunning = ref(false);

async function getFaxForms() {
  faxDestinationOptions.value = await mainServices.getFaxForms();
}

function clearSearch() {
  if (searchRunning.value) {
    searchRunning.value = false;
    searchFormData.value = {
      searchTerm: "",
      status: "",
      orderBy: "",
    };
    getAvailableOrders(1);
  }
}

function runSearch() {
  searchRunning.value = true;
  getAvailableOrders(1);
}

const statusOptionsWithCounts = computed(() => {
  return statusOptions.value.map((option) => {
    if (option.value === "") {
      return option; // Keep "All Submissions" as is
    }

    // Find count for this status
    const countItem = statusCounts.value.find((item) => item.status === option.value);
    const count = countItem ? countItem.count : 0;

    return {
      ...option,
      title: `${option.title} (${count})`,
    };
  });
});

const loadStatusCounts = async () => {
  const response = await orderInfoService.getOrderStatusCounts();
  statusCounts.value = response;
};

async function getAvailableOrders(index) {
  currentPage.value = index;
  preloader.value = true;
  const response = searchRunning.value
    ? await orderInfoService.searchOrders(searchFormData.value, index)
    : await orderInfoService.getListOfOrders(index);
  availableOrders.value = [...response.results];
  pageCount.value = response.count ? Math.ceil(response.count / 10) : 1;
  await new Promise((resolve) => setTimeout(resolve, 0));
  window.scrollTo(0, 0);
  preloader.value = false;
}
const displayedPages = computed(() => {
  const pages = [];

  pages.push(1, 2, 3);

  for (let i = currentPage.value - 3; i <= currentPage.value + 3; i++) {
    pages.push(i);
  }

  pages.push(pageCount.value - 2, pageCount.value - 1, pageCount.value);

  let uniquePages = [...new Set(pages)].filter((page) => page >= 1 && page <= pageCount.value);
  uniquePages.sort((a, b) => a - b);

  const finalPages = [];
  let lastPage = 0;
  uniquePages.forEach((page) => {
    if (page - lastPage > 1 && lastPage !== 0) {
      finalPages.push("...");
    }
    finalPages.push(page);
    lastPage = page;
  });

  return finalPages;
});

const onOrderDelete = () => {
  getAvailableOrders(currentPage.value);
};

const onUploadDecision = () => {
  getAvailableOrders(currentPage.value);
};
</script>

<style lang="scss" scoped>
@import "../styles/_variables.scss";
@import "../styles/typography.scss";
@import "../styles/pages/_submission-status.scss";

.skeleton-name {
  margin-bottom: 16px;
}

.v-skeleton-loader__button {
  margin-right: 8px;
}
</style>
